import { useState } from 'react';
import { toast } from 'sonner';
import { ERRORS } from '@/constants';
import { ApiError } from '@/services/api/client';
import { useCurrentUser, useLicence, useSettings, useUpdateSettings } from '@/services/queries';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Skeleton } from '@/shared/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import type { UpdateSettingsRequest } from '@/types';

/**
 * The customer portal's Settings, as the design's `settingGroups` draw them:
 * one card per group, a row per switch, a 46x26 track that is green when on.
 *
 * "Two-factor authentication" here is not an authenticator app. It is the
 * second check before a licence key is revealed - the password, asked again.
 * On for everyone by default; turning it off asks for the password once, so a
 * borrowed session cannot switch it off quietly.
 *
 * The design's red "Revoke all active sessions" card is deliberately absent.
 */

/** The design's switch. A real button with `role="switch"`, so it is keyboard- and
 *  screen-reader-operable, not just a painted track. */
function Toggle({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange?: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={cn(
        'relative h-[26px] w-[46px] flex-none rounded-[20px] transition-[background] duration-150',
        checked ? 'bg-[#15803D]' : 'bg-[#D7DEE8]',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
    >
      <span
        className={cn(
          'absolute top-[3px] size-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-[left] duration-150',
          checked ? 'left-[21px]' : 'left-[3px]',
        )}
      />
    </button>
  );
}

function Row({
  label,
  description,
  children,
}: {
  label: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-5 border-b border-[#F7F9FC] px-7 py-5 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">{label}</div>
        <div className="mt-[3px] text-[12.5px] leading-[1.5] text-[#8A94A6]">{description}</div>
      </div>
      {children}
    </div>
  );
}

function Group({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-[#E3E7ED] bg-card shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
      <div className="border-b border-[#F1F4F8] px-7 py-[22px]">
        <h2 className="text-base font-bold">{title}</h2>
        <p className="mt-1 text-[13px] text-[#8A94A6]">{subtitle}</p>
      </div>
      {children}
    </section>
  );
}

export function SettingsCards() {
  const { data: user } = useCurrentUser();
  const licence = useLicence().data?.licence ?? null;
  const settingsQuery = useSettings();
  const update = useUpdateSettings();
  const [confirmingOff, setConfirmingOff] = useState(false);

  if (settingsQuery.isPending) return <Skeleton className="h-80 w-full rounded-2xl" />;
  const settings = settingsQuery.data;
  if (!settings) return null;

  const save = (body: UpdateSettingsRequest, done?: string) => {
    update.mutate(body, {
      onSuccess: () => {
        if (done) toast.success(done);
      },
      onError: () => toast.error('Could not save that setting. Try again in a moment.'),
    });
  };

  const accountSubtitle = [user?.name, licence?.organizationName].filter(Boolean).join(' · ');

  return (
    <div className="flex flex-col gap-[18px]">
      <Group title="Account" subtitle={accountSubtitle || 'Your account'}>
        <Row
          label="Two-factor authentication"
          description="Asks for your password again before your licence key is revealed. When off, Reveal key shows the key straight away."
        >
          <Toggle
            label="Two-factor authentication"
            checked={settings.revealRequiresPassword}
            disabled={update.isPending}
            onChange={(next) => {
              // Turning it off proves the password first; turning it on is free.
              if (next) save({ revealRequiresPassword: true }, 'Password check turned on');
              else setConfirmingOff(true);
            }}
          />
        </Row>
        <Row
          label="Share anonymous usage data"
          description="Helps prioritise fixes; never includes patient data. Not collected at the moment."
        >
          {/* Always off: nothing collects usage data, so "on" would promise
              something the product does not do. */}
          <Toggle label="Share anonymous usage data" checked={settings.shareUsageData} disabled />
        </Row>
      </Group>

      <Group title="Notifications" subtitle={user ? `Email to ${user.email}` : 'Email'}>
        <Row label="Version update available" description="One email per release on your channel">
          <Toggle
            label="Version update available"
            checked={settings.emailUpdates}
            disabled={update.isPending}
            onChange={(next) => save({ emailUpdates: next })}
          />
        </Row>
        <Row label="Licence expiry reminders" description="60, 30 and 7 days before expiry">
          <Toggle
            label="Licence expiry reminders"
            checked={settings.emailExpiryReminders}
            disabled={update.isPending}
            onChange={(next) => save({ emailExpiryReminders: next })}
          />
        </Row>
      </Group>

      <TurnOffDialog open={confirmingOff} onOpenChange={setConfirmingOff} />
    </div>
  );
}

/** Password, once, to turn the reveal check off. Never kept. */
function TurnOffDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateSettings();
  const [password, setPassword] = useState('');

  const close = (next: boolean) => {
    if (!next) {
      setPassword('');
      update.reset();
    }
    onOpenChange(next);
  };

  const apiError = update.error instanceof ApiError ? update.error : null;
  const message = !apiError
    ? null
    : apiError.code === 'password_incorrect'
      ? ERRORS.passwordIncorrect
      : apiError.code === 'account_locked'
        ? ERRORS.accountLocked
        : apiError.code === 'rate_limited'
          ? ERRORS.rateLimited
          : apiError.message;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <form
          noValidate
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!password) return;
            update.mutate(
              { revealRequiresPassword: false, password },
              {
                onSuccess: () => {
                  close(false);
                  toast.success('Password check turned off');
                },
                onError: () => setPassword(''),
              },
            );
          }}
        >
          <DialogHeader>
            <DialogTitle>Turn off two-factor authentication?</DialogTitle>
            <DialogDescription>
              Your licence key will be shown without asking for your password. Enter your password
              to confirm.
            </DialogDescription>
          </DialogHeader>

          {message && (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              {message}
            </p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="settings-password" className="text-xs font-semibold text-[#48536A]">
              Password
            </Label>
            <Input
              id="settings-password"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="h-[42px] rounded-[9px] border-[1.5px] px-[13px]"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!password || update.isPending}>
              {update.isPending ? 'Checking…' : 'Turn off'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
